import { Flex, HStack, VStack, Box, Button, IconButton, Text, FormSpacer } from '@cad-arc';
import { BoloSearchOptions, EventSearchOptions } from '@mark43/cad-api';
import { useEffect } from 'react';
import { useWatch } from 'react-hook-form';
import { FormInput } from '../forms/integrated-form-controls/form-input';
import { FormNumberInput } from '../forms/integrated-form-controls/form-number-input';
import { FormRadioGroup } from '../forms/integrated-form-controls/form-radio-group';
import { FormSelect } from '../forms/integrated-form-controls/form-select';
import { FormVehicleMakeSelect } from '../forms/integrated-form-controls/form-vehicle-make-select';
import { FormVehicleModelSelect } from '../forms/integrated-form-controls/form-vehicle-model-select';
import { INPUT_WIDTH_SM } from '../search-windows/constants';
import { useIntegratedForm } from '../forms/use-integrated-form';
import { SearchVehiclesForm } from './types';

export function SearchFormVehicleFields({
    options,
    index,
    isRemovable,
    onRemove,
    hideTowTypeField,
    isFirstResponder,
}: {
    options: EventSearchOptions | BoloSearchOptions;
    index: number;
    isRemovable: boolean;
    onRemove: () => void;
    hideTowTypeField?: boolean;
    isFirstResponder?: boolean;
}) {
    const { form } = useIntegratedForm<SearchVehiclesForm>();
    const showInvolvementTypeOther = useWatch({
        control: form.control,
        name: `vehicles.${index}.involvementType`,
    })?.isOther;
    useEffect(
        function resetInvolvementTypeOther() {
            if (!showInvolvementTypeOther) {
                form.resetField(`vehicles.${index}.involvementTypeOther`);
            }
        },
        [form, index, showInvolvementTypeOther]
    );
    const showMakeOther = useWatch({
        control: form.control,
        name: `vehicles.${index}.vehicleMake`,
    })?.isOther;
    useEffect(
        function resetMakeOther() {
            if (!showMakeOther) {
                form.resetField(`vehicles.${index}.vehicleMakeOther`);
            }
        },
        [form, index, showMakeOther]
    );
    const showModelOther = useWatch({
        control: form.control,
        name: `vehicles.${index}.vehicleModel`,
    })?.isOther;
    useEffect(
        function resetModelOther() {
            if (!showModelOther) {
                form.resetField(`vehicles.${index}.vehicleModelOther`);
            }
        },
        [form, index, showModelOther]
    );
    return (
        <VStack align="left">
            {isRemovable && (
                <HStack justifyContent="space-between">
                    <Text variant="headingXs">Vehicle {index + 1}</Text>
                    <IconButton
                        variant="ghost"
                        icon="TrashCan"
                        aria-label="remove vehicle"
                        size={isFirstResponder ? 'lg' : 'sm'}
                        onClick={() => onRemove()}
                    />
                </HStack>
            )}
            <Flex flex={1} gap="1rem" flexFlow="wrap">
                <Box width="8rem">
                    <FormInput
                        name={`vehicles.${index}.plateNumber`}
                        label="Plate #"
                        control={form.control}
                    />
                </Box>
                <Box width="8rem">
                    <FormSelect
                        name={`vehicles.${index}.state`}
                        label="Plate State"
                        control={form.control}
                        options={options.stateOptions}
                    />
                </Box>

                <Box width="8rem">
                    <FormInput
                        name={`vehicles.${index}.vinNumber`}
                        label="VIN"
                        control={form.control}
                    />
                </Box>

                <Box width="8rem">
                    <FormVehicleMakeSelect
                        name={`vehicles.${index}.vehicleMake`}
                        label="Make"
                        control={form.control}
                    />
                </Box>
                {showMakeOther && (
                    <Box width="8rem">
                        <FormInput
                            name={`vehicles.${index}.vehicleMakeOther`}
                            label="Make Description"
                            control={form.control}
                            excludeFromFuzzySearch
                        />
                    </Box>
                )}
                <Box width="8rem">
                    <FormVehicleModelSelect
                        name={`vehicles.${index}.vehicleModel`}
                        label="Model"
                        control={form.control}
                    />
                </Box>
                {showModelOther && (
                    <Box width="8rem">
                        <FormInput
                            name={`vehicles.${index}.vehicleModelOther`}
                            label="Model Description"
                            control={form.control}
                            excludeFromFuzzySearch
                        />
                    </Box>
                )}
                <Box width="8rem">
                    <FormSelect
                        name={`vehicles.${index}.color`}
                        label="Color"
                        control={form.control}
                        options={options.itemColorOptions}
                    />
                </Box>

                <Box width="8rem">
                    <FormNumberInput
                        name={`vehicles.${index}.year`}
                        label="Year"
                        control={form.control}
                        numberInputProps={{ precision: 0 }}
                    />
                </Box>
                <Box width="8rem">
                    <FormInput
                        name={`vehicles.${index}.notes`}
                        label="Notes"
                        control={form.control}
                    />
                </Box>
                <Flex flex={1} gap="1rem" flexFlow="wrap" alignItems="end">
                    <Box width={INPUT_WIDTH_SM}>
                        <FormSelect
                            name={`vehicles.${index}.involvementType`}
                            label="Involvement Type"
                            control={form.control}
                            options={options.vehicleInvolvementTypeOptions}
                        />
                    </Box>
                    {showInvolvementTypeOther && (
                        <Box width={INPUT_WIDTH_SM}>
                            <FormInput
                                name={`vehicles.${index}.involvementTypeOther`}
                                label="Description"
                                control={form.control}
                            />
                        </Box>
                    )}
                </Flex>
            </Flex>
            {!hideTowTypeField && (
                <HStack>
                    <Box>
                        <FormSpacer alignItems="center">
                            <FormRadioGroup
                                name={`vehicles.${index}.towType`}
                                items={options.towTypeOptions}
                                control={form.control}
                            />
                        </FormSpacer>
                    </Box>
                    <Button
                        variant="ghost"
                        onClick={() => form.resetField(`vehicles.${index}.towType`)}
                    >
                        Clear
                    </Button>
                </HStack>
            )}
        </VStack>
    );
}
